// 📄 pages/SAP-BTP/components/SubirIflowModal.tsx
// Asistente de subida del Repositorio de iFlows: 1) elegir el .zip (se sube
// al staging propio y el backend lo analiza), 2) revisar el análisis y los
// avisos de valores sensibles, 3) completar los datos + documentación
// opcional y guardar. Nada queda guardado hasta el paso 3.
import React, { useEffect, useRef, useState } from 'react';
import { X, UploadCloud, RefreshCw, FileArchive } from 'lucide-react';
import {
  AnalisisIflow, DatosIflow, IflowApiError, LIMITE_ZIP_BYTES, MAX_DOCUMENTOS,
  analizarIflow, crearIflow, extensionDe, subirAStaging, tamanoLegible
} from '../../../services/iflowService.ts';
import { DetalleTecnico } from './DetalleTecnico.tsx';
import AvisosAnalisis from './AvisosAnalisis.tsx';
import FormDatosIflow, { labelCls } from './FormDatosIflow.tsx';
import SelectorDocumentos from './SelectorDocumentos.tsx';

interface SubirIflowModalProps {
  orgId: string;
  orgNombre: string;
  onCerrar: () => void;
  onCreado: () => void;
}

type Paso = 'archivo' | 'revision' | 'datos';

/** Zona para elegir el .zip; también la usa "Nueva versión". */
export function ElegirZip({ ocupado, nombre, onElegir }: { ocupado: boolean; nombre?: string; onElegir: (f: File | undefined) => void }) {
  return (
    <label className={`flex flex-col items-center justify-center text-center gap-3 p-10 rounded-2xl border-2 border-dashed border-white/15 cursor-pointer hover:border-cyan-500/40 transition-all ${ocupado ? 'pointer-events-none opacity-70' : ''}`}>
      {ocupado ? <RefreshCw className="w-8 h-8 text-cyan-400 animate-spin" /> : <UploadCloud className="w-8 h-8 text-cyan-400" />}
      <span className="text-sm text-white">{ocupado ? `Analizando ${nombre || 'el archivo'}…` : 'Elige el .zip exportado desde Integration Suite'}</span>
      <span className="text-xs text-gray-500">Un iFlow suelto (no un paquete completo), hasta 10 MB.</span>
      <input type="file" accept=".zip,application/zip" className="hidden" onChange={(e) => { onElegir(e.target.files?.[0]); e.target.value = ''; }} />
    </label>
  );
}

/** Valida, sube al staging y analiza un .zip. Devuelve la ruta y el análisis. */
export const prepararZip = async (orgId: string, file: File): Promise<{ stagingPath: string; analisis: AnalisisIflow }> => {
  if (extensionDe(file.name) !== 'zip') throw new Error('Elige el .zip exportado del iFlow.');
  if (file.size > LIMITE_ZIP_BYTES) throw new Error('El archivo supera los 10 MB.');
  const stagingPath = await subirAStaging(file);
  return { stagingPath, analisis: await analizarIflow(orgId, stagingPath) };
};

export default function SubirIflowModal({ orgId, orgNombre, onCerrar, onCreado }: SubirIflowModalProps) {
  const [paso, setPaso] = useState<Paso>('archivo');
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Solo el contenido hace scroll (encabezado con la X y pie siempre visibles);
  // si aparece un error, se vuelve arriba para que se vea.
  const cuerpoRef = useRef<HTMLDivElement>(null);
  useEffect(() => { if (error) cuerpoRef.current?.scrollTo({ top: 0, behavior: 'smooth' }); }, [error]);

  const [archivo, setArchivo] = useState<File | null>(null);
  const [stagingPath, setStagingPath] = useState('');
  const [analisis, setAnalisis] = useState<AnalisisIflow | null>(null);
  const [vaciarParametros, setVaciarParametros] = useState(true);
  const [confirmaFijos, setConfirmaFijos] = useState(false);

  const [datos, setDatos] = useState<DatosIflow>({ titulo: '', descripcion: '', estado: 'borrador', etiquetas: [] });
  const [documentos, setDocumentos] = useState<File[]>([]);

  const elegirZip = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setArchivo(file);
    setOcupado(true);
    try {
      const r = await prepararZip(orgId, file);
      setStagingPath(r.stagingPath);
      setAnalisis(r.analisis);
      setConfirmaFijos(false);
      setDatos((d) => ({ ...d, titulo: d.titulo || r.analisis.nombreTecnico || r.analisis.idTecnico, descripcion: d.descripcion || r.analisis.descripcionTenant }));
      setPaso('revision');
    } catch (err: any) {
      setArchivo(null);
      setError(err.message || 'No se pudo analizar el archivo.');
    } finally {
      setOcupado(false);
    }
  };

  const guardar = async () => {
    if (!analisis) return;
    if (!datos.titulo.trim() || !datos.descripcion.trim()) return setError('El título y la descripción son obligatorios.');
    setError(null);
    setOcupado(true);
    try {
      const docs = await Promise.all(documentos.map(async (f) => ({ stagingPath: await subirAStaging(f), name: f.name })));
      await crearIflow({ orgId, stagingPath, data: datos, documents: docs, stripParameterValues: vaciarParametros, confirmFixedValues: confirmaFijos });
      onCreado();
    } catch (err: any) {
      if (err instanceof IflowApiError && err.code === 'FIXED_VALUES_CONFIRMATION_REQUIRED') {
        setPaso('revision');
        setConfirmaFijos(false);
      }
      setError(err.message || 'No se pudo guardar el iFlow.');
    } finally {
      setOcupado(false);
    }
  };

  const puedeSeguir = !analisis?.valoresFijos.length || confirmaFijos;

  return (
    <div className="fixed inset-0 bg-black/70 flex justify-center items-center z-50 p-4">
      <div className="bg-gray-800 border border-white/10 rounded-2xl w-full max-w-3xl max-h-[calc(100vh-2rem)] flex flex-col">
        <header className="flex items-start justify-between gap-4 p-5 border-b border-white/10 flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-white">Subir iFlow</h2>
            <p className="text-xs text-gray-400 mt-0.5">Se guardará en: <span className="text-cyan-300 font-medium">{orgNombre}</span></p>
          </div>
          <button onClick={onCerrar} disabled={ocupado} className="text-gray-400 hover:text-white disabled:opacity-40" aria-label="Cerrar">
            <X className="w-5 h-5" />
          </button>
        </header>

        <ol className="flex gap-2 px-5 pt-4 text-xs flex-shrink-0">
          {(['archivo', 'revision', 'datos'] as Paso[]).map((p, i) => (
            <li key={p} className={`flex-1 border-t-2 pt-2 ${paso === p ? 'border-cyan-400 text-cyan-300' : 'border-white/10 text-gray-500'}`}>
              {i + 1}. {p === 'archivo' ? 'Archivo' : p === 'revision' ? 'Revisión' : 'Datos y documentación'}
            </li>
          ))}
        </ol>

        <div ref={cuerpoRef} className="p-5 space-y-4 overflow-y-auto min-h-0 flex-1">
          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

          {paso === 'archivo' && <ElegirZip ocupado={ocupado} nombre={archivo?.name} onElegir={elegirZip} />}

          {paso === 'revision' && analisis && (
            <>
              <div className="flex items-center gap-3 text-sm">
                <FileArchive className="w-5 h-5 text-cyan-400" />
                <span className="text-white font-medium">{archivo?.name}</span>
                <span className="text-gray-500">{tamanoLegible(analisis.tamano)}</span>
              </div>
              <DetalleTecnico {...analisis} />
              <AvisosAnalisis
                analisis={analisis}
                confirmaFijos={confirmaFijos}
                onConfirmaFijos={setConfirmaFijos}
                vaciarParametros={vaciarParametros}
                onVaciarParametros={setVaciarParametros}
              />
            </>
          )}

          {paso === 'datos' && (
            <>
              <FormDatosIflow orgId={orgId} datos={datos} onChange={setDatos} />
              <div>
                <label className={labelCls}>Documentación técnica (opcional, hasta {MAX_DOCUMENTOS})</label>
                <SelectorDocumentos archivos={documentos} onChange={setDocumentos} onError={setError} />
              </div>
            </>
          )}
        </div>

        <footer className="flex justify-between gap-3 p-5 border-t border-white/10 flex-shrink-0">
          <button
            onClick={() => (paso === 'datos' ? setPaso('revision') : paso === 'revision' ? setPaso('archivo') : onCerrar())}
            disabled={ocupado}
            className="px-4 py-2 text-sm text-gray-300 hover:text-white disabled:opacity-40"
          >
            {paso === 'archivo' ? 'Cancelar' : 'Atrás'}
          </button>
          {paso === 'revision' && (
            <button onClick={() => setPaso('datos')} disabled={!puedeSeguir} className="px-5 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium disabled:opacity-40">
              Continuar
            </button>
          )}
          {paso === 'datos' && (
            <button onClick={guardar} disabled={ocupado} className="px-5 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium disabled:opacity-40 inline-flex items-center gap-2">
              {ocupado && <RefreshCw className="w-4 h-4 animate-spin" />} Guardar iFlow
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}
